import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import { Empty, Loading, apiError, monthOf, saveBlob, ui, useCampus, useIdleLocked, usePerms } from "../kits/shared";
import { parseDecimal } from "../../../../components/kits/kitUi";
import { tituloEstado } from "../../../../utils/tituloEstado";

/*
	Tienda → Metas y Bonos (sección "salesGoals", fase 2, 2026-10-01). Desde Módulos Claros (2026-11-01) es la pestaña «Bonos» de
	Ventas (solo quien tiene la sección 67): Cierre del Mes (en vivo hasta que Dirección de Kits lo aprueba; Contraloría lo marca
	pagado) · Metas por plantel · Reglas de Bono. El Ranking pasó a Ventas › Vendedores. La lógica no cambió.
*/

type Tab = "month" | "goals" | "rules" | "ranking";

const previousMonth = (): string => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return monthOf(d); };

/** Number fields typed by people: "12,5" is 12.5; empty stays empty; something else is an error (never sent as empty). */
const numberField = (text: any): { value: string; invalid: boolean } => {
	const parsed = parseDecimal(text === null || text === undefined ? "" : String(text));
	if (parsed === null) return { value: "", invalid: false };
	return isNaN(parsed) ? { value: String(text), invalid: true } : { value: String(parsed), invalid: false };
};
const pctText = (value: number | null | undefined) => value === null || value === undefined ? "-" : value.toFixed(1) + "%";

const MetasBonos: React.FC<{ embebido?: boolean }> = ({ embebido }) => {
	const { campusId, currency } = useCampus();
	const [tab, setTab] = useState<Tab>("month");
	const idleLocked = useIdleLocked();
	const money = (value: number | null | undefined) => value === null || value === undefined ? "-" : currency + value.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	if (!campusId) return <div style={ui.page}>Selecciona un plantel.</div>;
	const pestanas: [Tab, string][] = [["month", "Cierre del Mes"], ["goals", "Metas"], ["rules", "Reglas de Bono"]];
	if (!embebido) pestanas.push(["ranking", "Ranking"]);
	return <div style={embebido ? { marginTop: 14 } : ui.page}>
		{embebido ? null : <h2 style={ui.title}>Metas y Bonos</h2>}
		<div className="waFmSegs" style={{ marginBottom: 10 }}>
			{pestanas.map(([key, label]) =>
				<button key={key} type="button" disabled={idleLocked} className={"waFmSeg" + (key === tab ? " activo" : "")} onClick={() => setTab(key)}>{label}</button>)}
		</div>
		{tab === "month" ? <MonthClose campusId={campusId} money={money} /> : tab === "goals" ? <Goals campusId={campusId} /> : tab === "rules" ? <Rules campusId={campusId} /> : <Ranking campusId={campusId} money={money} />}
	</div>;
};

// ------------------------------------------------------------------ cierre del mes

const MonthClose: React.FC<{ campusId: number; money: (value: number | null | undefined) => string }> = ({ campusId, money }) => {
	const http = HttpManager.getInstance().salesService;
	const perms: any = usePerms("salesGoals");
	const idleLocked = useIdleLocked();
	const currentUser: any = useSelector((state: RootState) => state.userProfile.value);
	const isSuper = currentUser?.currentRole === "superAdmin";
	const [month, setMonth] = useState<string>(previousMonth());
	const [data, setData] = useState<any | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [detail, setDetail] = useState<{ person: any; rows: any[] | null } | null>(null);
	const load = useCallback(() => {
		setData(null);
		setError(null);
		http.month(campusId, month).then((response) => setData(response.data)).catch((requestError) => setError(apiError(requestError, "No se pudo calcular el mes.")));
	}, [campusId, month]);
	useEffect(() => { load(); }, [load]);
	const act = async (fn: () => Promise<any>) => {
		setBusy(true);
		setError(null);
		try { setData((await fn()).data); } catch (requestError) { setError(apiError(requestError, "No se pudo completar la acción.")); } finally { setBusy(false); }
	};
	const [detailError, setDetailError] = useState<string | null>(null);
	const openDetail = (person: any) => {
		setDetail({ person, rows: null });
		setDetailError(null);
		http.detail(campusId, month, person.key).then((response) => setDetail({ person, rows: response.data }))
			.catch((requestError) => { setDetail({ person, rows: [] }); setDetailError(apiError(requestError, "No se pudieron cargar los renglones.")); });
	};
	// The server says who can approve / pay (same role rules it enforces); older answers fall back to the section bits.
	const canApprove = data ? (data.canApprove ?? (data.live && !data.isOpen && !!perms?.create)) : false;
	const canPay = data ? (data.canPay ?? (data.statusId === 1 && !!perms?.create)) : false;
	const canReopen = data ? (data.canReopen ?? (data.statusId === 1 && isSuper)) : false;
	const metrics = data?.metrics;
	const kpi = (caption: string, value: string, goal: string | null, pct: number | null | undefined) => <div style={ui.kpi}>
		<div style={ui.caption}>{caption}</div>
		<div style={{ fontSize: "20px", fontWeight: 700 }}>{value}</div>
		{goal ? <div style={pct === null || pct === undefined || pct >= 100 ? ui.tiny : { ...ui.tiny, color: "var(--wa-oro)" }}>Meta {goal}{pct !== null && pct !== undefined ? ", " + pctText(pct) : ""}</div> : null}
	</div>;
	return <>
		<div style={{ ...ui.card, ...ui.filters }}>
			<div style={ui.field("150px")}><span style={ui.caption}>Mes</span>
				<input type="month" className="form-control" style={ui.input} value={month} disabled={idleLocked} onChange={(event) => setMonth(event.target.value)} /></div>
			{data ? <span style={{ ...ui.badge(false), fontSize: "13px", padding: "3px 10px", background: data.statusId === 2 ? "var(--wa-papel-3)" : data.statusId === 1 ? "var(--wa-azul-suave)" : "var(--wa-oro-suave)" }}>
				{data.isOpen ? "Mes en Curso" : tituloEstado(data.status)}</span> : null}
			{data?.approvedBy ? <span style={ui.small}>Aprobó {data.approvedBy} · {data.approvedAt}</span> : null}
			{data?.paidBy ? <span style={ui.small}>Pagado por {data.paidBy} · {data.paidAt}</span> : null}
			<div style={{ marginLeft: "auto", display: "flex", gap: "6px" }}>
				{canApprove ? <button type="button" className={ui.btnPrimary} disabled={busy || idleLocked} onClick={() => act(() => http.approve(campusId, month))}>Aprobar</button> : null}
				{canPay ? <button type="button" className={ui.btnPrimary} disabled={busy || idleLocked} onClick={() => act(() => http.markPaid(campusId, month))}>Marcar Pagado</button> : null}
				{canReopen ? <button type="button" className={ui.btn} disabled={busy || idleLocked} onClick={() => act(() => http.reopen(campusId, month))}>Reabrir</button> : null}
				<button type="button" className={ui.btn} disabled={!data || idleLocked} onClick={() => { setError(null); http.bonusExcel(campusId, month).then((response) => saveBlob(response.data, "Bonos_" + month + ".xlsx", "Ventas de tienda")).catch((requestError) => setError(apiError(requestError, "No se pudo generar el Excel."))); }}>Excel</button>
			</div>
		</div>
		{error ? <div className="alert alert-danger" style={{ marginTop: "8px" }}>{error}</div> : null}
		{metrics ? <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", margin: "10px 0" }}>
			{kpi("Kits Completos", String(metrics.kits), metrics.goal.kits !== null ? String(metrics.goal.kits) : null, metrics.kitsPct)}
			{kpi("Cobrado", money(metrics.amount), metrics.goal.amount !== null ? money(metrics.goal.amount) : null, metrics.amountPct)}
			{kpi("Inscritas del Mes con Kit", pctText(metrics.kitRate), metrics.goal.kitRate !== null ? pctText(metrics.goal.kitRate) : null,
				metrics.goal.kitRate && metrics.kitRate !== null ? metrics.kitRate * 100 / metrics.goal.kitRate : null)}
			<div style={ui.kpi}><div style={ui.caption}>Bonos del Mes</div>
				<div style={{ fontSize: "20px", fontWeight: 700 }}>{money((data.people || []).reduce((sum: number, person: any) => sum + person.total, 0))}</div>
				<div style={ui.tiny}>{data.rules ?? 0} {data.rules === 1 ? "Regla" : "Reglas"}</div></div>
		</div> : null}
		<div className="waScrollX" style={{ ...ui.card, overflowX: "auto" }}>
			{data === null ? <Loading /> : (data.people || []).length < 1 ? <Empty text="Sin ventas en el mes." /> : <table style={{ width: "100%", borderCollapse: "collapse" }}>
				<thead><tr>
					<th style={ui.th}>Persona</th><th style={ui.th}>Tipo</th>
					<th style={{ ...ui.th, ...ui.right }}>Ventas</th><th style={{ ...ui.th, ...ui.right }}>Kits</th><th style={{ ...ui.th, ...ui.right }}>Cobrado</th>
					<th style={{ ...ui.th, ...ui.right }}>Bono</th><th style={{ ...ui.th, ...ui.right }}>Ajustes</th><th style={{ ...ui.th, ...ui.right }}>Total</th>
				</tr></thead>
				<tbody>{data.people.map((person: any) => <tr key={person.audience + person.key} style={{ cursor: "pointer" }} onClick={() => openDetail(person)}>
					<td style={ui.td}><b>{person.name}</b>{person.role && person.audience === "seller" ? <div style={ui.tiny}>{person.role}</div> : null}</td>
					<td style={ui.td}>{person.audience === "teacher" ? "Maestra" : "Asesora"}</td>
					<td style={{ ...ui.td, ...ui.right }}>{person.sales}</td>
					<td style={{ ...ui.td, ...ui.right }}>{person.kits}</td>
					<td style={{ ...ui.td, ...ui.right }}>{money(person.base)}</td>
					<td style={{ ...ui.td, ...ui.right }}>{money(person.bonus)}</td>
					<td style={{ ...ui.td, ...ui.right, color: person.adjustments < 0 ? "var(--wa-oro)" : undefined }}>{person.adjustments ? money(person.adjustments) : "-"}</td>
					<td style={{ ...ui.td, ...ui.right, fontWeight: 700 }}>{money(person.total)}</td>
				</tr>)}</tbody>
			</table>}
		</div>
		{detail ? <div style={{ position: "fixed", inset: 0, background: "rgba(43,28,22,0.45)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }} onClick={() => setDetail(null)}>
			<div className="card" style={{ width: "100%", maxWidth: "860px", maxHeight: "90vh", overflowY: "auto" }} onClick={(event) => event.stopPropagation()}>
				<div className="card-body">
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
						<h5 style={{ margin: 0 }}>{detail.person.name} <span style={ui.small}>{month}</span></h5>
						<button type="button" className={ui.btn} onClick={() => setDetail(null)}>Cerrar</button>
					</div>
					{detailError ? <div className="alert alert-danger" style={{ marginTop: "8px" }}>{detailError}</div> : null}
					{detail.rows === null ? <Loading /> : detail.rows.length < 1 ? (detailError ? null : <Empty text="Sin Bono." />) : <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px" }}>
						<thead><tr><th style={ui.th}>Venta</th><th style={ui.th}>Regla</th><th style={{ ...ui.th, ...ui.right }}>Base</th><th style={{ ...ui.th, ...ui.right }}>Parte</th><th style={{ ...ui.th, ...ui.right }}>Monto</th></tr></thead>
						<tbody>{detail.rows.map((row: any, index: number) => <tr key={index}>
							<td style={ui.td}>{row.kind === "adjustment" ? <span style={{ color: "var(--wa-oro)" }}>{row.note}</span> : row.note}</td>
							<td style={ui.td}>{row.rule || "-"}</td>
							<td style={{ ...ui.td, ...ui.right }}>{money(row.base)}</td>
							<td style={{ ...ui.td, ...ui.right }}>{row.share < 1 ? Math.round(row.share * 100) + "%" : ""}</td>
							<td style={{ ...ui.td, ...ui.right, fontWeight: 600 }}>{money(row.amount)}</td>
						</tr>)}</tbody>
					</table>}
				</div>
			</div>
		</div> : null}
	</>;
};

// ------------------------------------------------------------------ metas

const Goals: React.FC<{ campusId: number }> = ({ campusId }) => {
	const http = HttpManager.getInstance().salesService;
	const perms: any = usePerms("salesGoals");
	const idleLocked = useIdleLocked();
	const [year, setYear] = useState<number>(new Date().getFullYear());
	const [rows, setRows] = useState<any[] | null>(null);
	const [selected, setSelected] = useState<number>(campusId);
	const [edits, setEdits] = useState<Record<string, any>>({});
	const [error, setError] = useState<string | null>(null);
	const [saved, setSaved] = useState<string | null>(null);
	useEffect(() => {
		setRows(null);
		http.goals(campusId, year).then((response) => setRows(response.data)).catch((requestError) => setError(apiError(requestError, "No se pudieron cargar las metas.")));
	}, [campusId, year]);
	const campus = (rows || []).find((row) => row.campusId === selected) || (rows || [])[0];
	const value = (month: any, key: string) => edits[month.month]?.[key] ?? (month[key] ?? "");
	const setValue = (month: string, key: string, next: string) => setEdits({ ...edits, [month]: { ...(edits[month] || {}), [key]: next } });
	const [savingMonth, setSavingMonth] = useState<string | null>(null);
	const save = (month: any) => {
		setError(null);
		const kits = numberField(value(month, "kitsTarget"));
		const amount = numberField(value(month, "amountTarget"));
		const rate = numberField(value(month, "kitRateTarget"));
		if (kits.invalid || amount.invalid || rate.invalid) {
			setError("Revisa las metas de " + month.month + ": solo números.");
			return;
		}
		setSavingMonth(month.month);
		const payload = { targetCampusId: campus.campusId, month: month.month, kitsTarget: kits.value, amountTarget: amount.value, kitRateTarget: rate.value, note: value(month, "note") };
		http.saveGoal(campusId, payload).then((response) => {
			setRows(response.data);
			const next = { ...edits };
			delete next[month.month];
			setEdits(next);
			setSaved(month.month);
		}).catch((requestError) => setError(apiError(requestError, "No se pudo guardar.")))
			.finally(() => setSavingMonth(null));
	};
	const canEdit = !!perms?.update && !idleLocked;
	return <>
		<div style={{ ...ui.card, ...ui.filters }}>
			<div style={ui.field("110px")}><span style={ui.caption}>Año</span>
				<select className="form-select" style={ui.input} value={year} onChange={(event) => setYear(Number(event.target.value))}>
					{[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
				</select></div>
			<div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
				{(rows || []).map((row) => <button key={row.campusId} type="button" className={"btn btn-sm rounded-pill " + (campus && row.campusId === campus.campusId ? "btn-primary" : "btn-outline-secondary")}
					onClick={() => setSelected(row.campusId)}>{row.campus.replace("Washington Academy ", "")}</button>)}
			</div>
		</div>
		{error ? <div className="alert alert-danger" style={{ marginTop: "8px" }}>{error}</div> : null}
		<div className="waScrollX" style={{ ...ui.card, overflowX: "auto", marginTop: "10px" }}>
			{rows === null ? <Loading /> : !campus ? <Empty /> : <table style={{ width: "100%", borderCollapse: "collapse" }}>
				<thead><tr><th style={ui.th}>Mes</th><th style={ui.th}>Kits Completos</th><th style={ui.th}>Cobrado</th><th style={ui.th}>% Inscritas con Kit</th><th style={ui.th}>Nota</th><th style={ui.th}></th></tr></thead>
				<tbody>{campus.months.map((month: any) => <tr key={month.month}>
					<td style={{ ...ui.td, whiteSpace: "nowrap", fontWeight: 600 }}>{month.month}</td>
					{["kitsTarget", "amountTarget", "kitRateTarget"].map((key) => <td key={key} style={ui.td}>
							<input type="text" inputMode={key === "kitsTarget" ? "numeric" : "decimal"} className="form-control" disabled={!canEdit}
								style={{ ...ui.input, width: "130px", ...(numberField(value(month, key)).invalid ? { borderColor: "var(--wa-oro)" } : {}) }}
								value={value(month, key)} onChange={(event) => setValue(month.month, key, event.target.value)} /></td>)}
					<td style={ui.td}><input className="form-control" style={{ ...ui.input, minWidth: "160px" }} disabled={!canEdit} value={value(month, "note")} onChange={(event) => setValue(month.month, "note", event.target.value)} /></td>
					<td style={{ ...ui.td, whiteSpace: "nowrap" }}>{edits[month.month] ? <button type="button" className={ui.btnPrimary} disabled={!canEdit || savingMonth !== null} onClick={() => save(month)}>{savingMonth === month.month ? "Guardando…" : "Guardar"}</button>
						: saved === month.month ? <span style={ui.small}>Guardado</span> : null}</td>
				</tr>)}</tbody>
			</table>}
		</div>
	</>;
};

// ------------------------------------------------------------------ reglas

const emptyRule = () => ({ targetCampusId: 0, audience: "seller", roleName: "", kind: "percent", scope: "kit", rate: "", rateAbove: "", goalMetric: "kit_rate", threshold: "", fixedAmount: "", split: "team", validFrom: monthOf() + "-01", validTo: "", note: "" });

const Rules: React.FC<{ campusId: number }> = ({ campusId }) => {
	const http = HttpManager.getInstance().salesService;
	const perms: any = usePerms("salesGoals");
	const idleLocked = useIdleLocked();
	const [data, setData] = useState<any | null>(null);
	const [form, setForm] = useState<any | null>(null);
	const [error, setError] = useState<string | null>(null);
	const load = useCallback(() => { http.rules(campusId).then((response) => setData(response.data)).catch((requestError) => setError(apiError(requestError, "No se pudieron cargar las reglas."))); }, [campusId]);
	useEffect(() => { load(); }, [load]);
	// Each button with the bit of the verb it calls: new rule = POST (C), edit = PUT (U), deactivate = DELETE (D).
	const canCreate = !!perms?.create && !idleLocked;
	const canEdit = !!perms?.update && !idleLocked;
	const canDeactivate = !!perms?.delete && !idleLocked;
	const [busy, setBusy] = useState<boolean>(false);
	const save = () => {
		if (busy) return;
		setError(null);
		const numbers: Record<string, string> = {};
		for (const key of ["rate", "rateAbove", "threshold", "fixedAmount"]) {
			const parsed = numberField(form[key]);
			if (parsed.invalid) { setError("Revisa los números de la regla: solo números."); return; }
			numbers[key] = parsed.value;
		}
		setBusy(true);
		http.saveRule(campusId, form.id || null, { ...form, ...numbers, active: form.active ?? true }).then(() => { setForm(null); load(); })
			.catch((requestError) => setError(apiError(requestError, "No se pudo guardar la regla.")))
			.finally(() => setBusy(false));
	};
	const deactivate = (rule: any) => {
		if (busy) return;
		setError(null);
		setBusy(true);
		http.deactivateRule(campusId, rule.id).then(load)
			.catch((requestError) => setError(apiError(requestError, "No se pudo desactivar la regla.")))
			.finally(() => setBusy(false));
	};
	const field = (caption: string, width: string, input: React.ReactNode) => <div style={ui.field(width)}><span style={ui.caption}>{caption}</span>{input}</div>;
	const set = (key: string, value: any) => setForm({ ...form, [key]: value });
	return <>
		{error ? <div className="alert alert-danger" style={{ marginTop: "8px" }}>{error}</div> : null}
		{canCreate && !form ? <button type="button" className={ui.btnPrimary} style={{ marginBottom: "10px" }} onClick={() => setForm({ ...emptyRule(), targetCampusId: campusId })}>Nueva Regla</button> : null}
		{form && data ? <div style={{ ...ui.card, ...ui.filters, marginBottom: "10px" }}>
			{field("Plantel", "200px", <select className="form-select" style={ui.input} value={form.targetCampusId} onChange={(event) => set("targetCampusId", Number(event.target.value))}>
				{data.campuses.map((campus: any) => <option key={campus.id} value={campus.id}>{campus.name.replace("Washington Academy ", "")}</option>)}</select>)}
			{field("Para", "140px", <select className="form-select" style={ui.input} value={form.audience} onChange={(event) => set("audience", event.target.value)}>
				{Object.entries(data.audiences).map(([key, label]) => <option key={key} value={key}>{label as string}</option>)}</select>)}
			{form.audience === "seller" ? field("Rol", "220px", <select className="form-select" style={ui.input} value={form.roleName || ""} onChange={(event) => set("roleName", event.target.value)}>
				<option value="">Todas</option>{data.sellerRoles.map((role: string) => <option key={role} value={role}>{role}</option>)}</select>) : null}
			{field("Tipo", "260px", <select className="form-select" style={ui.input} value={form.kind} onChange={(event) => set("kind", event.target.value)}>
				{Object.entries(data.kinds).map(([key, label]) => <option key={key} value={key}>{label as string}</option>)}</select>)}
			{form.kind !== "fixed" ? <>
				{field("Productos", "150px", <select className="form-select" style={ui.input} value={form.scope} onChange={(event) => set("scope", event.target.value)}>
					{Object.entries(data.scopes).map(([key, label]) => <option key={key} value={key}>{label as string}</option>)}</select>)}
				{field(form.kind === "tiered" ? "% Hasta la Meta" : "%", "130px", <input type="text" inputMode="decimal" className="form-control" style={ui.input} value={form.rate} onChange={(event) => set("rate", event.target.value)} />)}
				{form.kind === "tiered" ? field("% Arriba de la Meta", "150px", <input type="text" inputMode="decimal" className="form-control" style={ui.input} value={form.rateAbove} onChange={(event) => set("rateAbove", event.target.value)} />) : null}
			</> : <>
				{field("Cuando", "200px", <select className="form-select" style={ui.input} value={form.goalMetric} onChange={(event) => set("goalMetric", event.target.value)}>
					{Object.entries(data.metrics).map(([key, label]) => <option key={key} value={key}>{label as string}</option>)}</select>)}
				{field("Llegue a", "100px", <input type="text" inputMode="decimal" className="form-control" style={ui.input} value={form.threshold} onChange={(event) => set("threshold", event.target.value)} />)}
				{field("Monto", "130px", <input type="text" inputMode="decimal" className="form-control" style={ui.input} value={form.fixedAmount} onChange={(event) => set("fixedAmount", event.target.value)} />)}
				{field("Se Paga", "190px", <select className="form-select" style={ui.input} value={form.split} onChange={(event) => set("split", event.target.value)}>
					<option value="team">Repartido en el Equipo</option><option value="each">A Cada Quien</option></select>)}
			</>}
			{field("Desde", "150px", <input type="date" className="form-control" style={ui.input} value={form.validFrom} onChange={(event) => set("validFrom", event.target.value)} />)}
			{field("Hasta", "150px", <input type="date" className="form-control" style={ui.input} value={form.validTo || ""} onChange={(event) => set("validTo", event.target.value)} />)}
			{field("Nota", "200px", <input className="form-control" style={ui.input} value={form.note || ""} onChange={(event) => set("note", event.target.value)} />)}
			<div style={{ display: "flex", gap: "6px" }}>
				<button type="button" className={ui.btnPrimary} style={{ height: "34px" }} disabled={busy} onClick={save}>{busy ? "Guardando…" : "Guardar"}</button>
				<button type="button" className={ui.btn} style={{ height: "34px" }} disabled={busy} onClick={() => setForm(null)}>Cancelar</button>
			</div>
		</div> : null}
		<div className="waScrollX" style={{ ...ui.card, overflowX: "auto" }}>
			{data === null ? <Loading /> : data.rules.length < 1 ? <Empty text="Sin Reglas." /> : <table style={{ width: "100%", borderCollapse: "collapse" }}>
				<thead><tr><th style={ui.th}>Regla</th><th style={ui.th}>Para</th><th style={ui.th}>Plantel</th><th style={ui.th}>Vigencia</th><th style={ui.th}></th></tr></thead>
				<tbody>{data.rules.map((rule: any) => <tr key={rule.id} style={{ opacity: rule.active ? 1 : 0.5 }}>
					<td style={ui.td}><b>{rule.summary}</b>{rule.note ? <div style={ui.tiny}>{rule.note}</div> : null}</td>
					<td style={ui.td}>{rule.audienceName}{rule.roleName ? <div style={ui.tiny}>{rule.roleName}</div> : null}</td>
					<td style={ui.td}>{(rule.campus || "").replace("Washington Academy ", "")}</td>
					<td style={{ ...ui.td, whiteSpace: "nowrap" }}>{rule.validFrom}{rule.validTo ? " a " + rule.validTo : " en Adelante"}{rule.active ? "" : ", Inactiva"}</td>
					<td style={{ ...ui.td, whiteSpace: "nowrap" }}>{canEdit || canDeactivate ? <>
							{canEdit ? <button type="button" className={ui.btn} disabled={busy} onClick={() => setForm({ ...rule, targetCampusId: rule.campusId, rate: rule.rate ?? "", rateAbove: rule.rateAbove ?? "", threshold: rule.threshold ?? "", fixedAmount: rule.fixedAmount ?? "", goalMetric: rule.goalMetric || "kit_rate", roleName: rule.roleName || "",validTo: rule.validTo || "" })}>Editar</button> : null}
							{rule.active && canDeactivate ? <button type="button" className={ui.btn} style={{ marginLeft: "4px" }} disabled={busy} onClick={() => deactivate(rule)}>Desactivar</button> : null}
					</> : null}</td>
				</tr>)}</tbody>
			</table>}
		</div>
	</>;
};

// ------------------------------------------------------------------ ranking

export const Ranking: React.FC<{ campusId: number; money: (value: number | null | undefined) => string }> = ({ campusId, money }) => {
	const http = HttpManager.getInstance().salesService;
	const [month, setMonth] = useState<string>(monthOf());
	const [data, setData] = useState<any | null>(null);
	const [error, setError] = useState<string | null>(null);
	useEffect(() => {
		setData(null);
		setError(null);
		http.ranking(campusId, month).then((response) => setData(response.data))
			.catch((requestError) => { setError(apiError(requestError, "No se pudo cargar el ranking.")); setData({ seller: [], teacher: [], campuses: [] }); });
	}, [campusId, month]);
	const table = (title: string, people: any[]) => <div className="waScrollX" style={{ ...ui.card, overflowX: "auto", flex: "1 1 420px" }}>
		<div style={{ padding: "10px 12px 0", fontWeight: 700 }}>{title}</div>
		{people.length < 1 ? <Empty text="Sin ventas atribuidas." /> : <table style={{ width: "100%", borderCollapse: "collapse" }}>
			<thead><tr><th style={ui.th}>#</th><th style={ui.th}>Persona</th><th style={ui.th}>Plantel</th><th style={{ ...ui.th, ...ui.right }}>Kits</th><th style={{ ...ui.th, ...ui.right }}>Cobrado</th><th style={{ ...ui.th, ...ui.right }}>Bono</th></tr></thead>
			<tbody>{people.map((person: any, index: number) => <tr key={person.key + person.campus}>
				<td style={ui.td}>{index + 1}</td><td style={ui.td}><b>{person.name}</b></td><td style={ui.td}>{(person.campus || "").replace("Washington Academy ", "")}</td>
				<td style={{ ...ui.td, ...ui.right }}>{person.kits}</td><td style={{ ...ui.td, ...ui.right }}>{money(person.base)}</td><td style={{ ...ui.td, ...ui.right }}>{money(person.total)}</td>
			</tr>)}</tbody>
		</table>}
	</div>;
	return <>
		<div style={{ ...ui.card, ...ui.filters }}>
			<div style={ui.field("150px")}><span style={ui.caption}>Mes</span><input type="month" className="form-control" style={ui.input} value={month} onChange={(event) => setMonth(event.target.value)} /></div>
		</div>
		{error ? <div className="alert alert-danger" style={{ marginTop: "8px" }}>{error}</div> : null}
		{data === null ? <Loading /> : <>
			<div style={{ display: "flex", gap: "8px", flexWrap: "wrap", margin: "10px 0" }}>
				{data.campuses.map((campus: any) => <div key={campus.campusId} style={ui.kpi}>
					<div style={ui.caption}>{campus.campus.replace("Washington Academy ", "")}</div>
					<div style={{ fontSize: "20px", fontWeight: 700 }}>{campus.metrics?.kits ?? 0} kits</div>
					<div style={ui.tiny}>{money(campus.metrics?.amount)} · {pctText(campus.metrics?.kitRate)} con kit · {campus.status}</div>
				</div>)}
			</div>
			<div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>{table("Vendedoras", data.seller)}{table("Maestros", data.teacher)}</div>
		</>}
	</>;
};

export default MetasBonos;
